import React, { Dispatch, ReactNode, SetStateAction, createContext, useCallback, useContext, useEffect, useState } from "react";

import { getMinVersion } from "services";

import {
  getLocalStorageImpactPanelCollapsed,
  setLocalStorageImpactPanelCollapsed,
  useAppType,
  useCancelAsyncOnUnmount,
  useNavigate,
  usePointPrice,
} from "utils";

import { PointPrices } from "models";

import { appVersions } from "enums";

import { NavigationCrumbs } from "components";

type GeneralContextType = {
  railNavCollapsed: boolean;
  setRailNavCollapsed: Dispatch<SetStateAction<boolean>>;
  impactPanelCollapsed: boolean;
  setImpactPanelCollapsed: Dispatch<SetStateAction<boolean>>;
  fullWidthPage: boolean;
  setFullWidthPage: Dispatch<SetStateAction<boolean>>;
  referralBanner: { text: string } | null;
  setReferralBanner: Dispatch<SetStateAction<{ text: string } | null>>;
  networkState: boolean;
  pointPrices: PointPrices;
  navigationCrumbs: NavigationCrumbs;
  setNavigationCrumbs: Dispatch<SetStateAction<NavigationCrumbs>>;
};

// check network state (online/offline)
export function useNetworkState() {
  const [networkState, setNetworkState] = useState(window.navigator.onLine);

  const checkNetworkStatus = () => {
    setNetworkState(window.navigator.onLine);
  };

  useEffect(() => {
    window.addEventListener("online", checkNetworkStatus);
    window.addEventListener("offline", checkNetworkStatus);
  }, []); // eslint-disable-line

  return networkState;
}

// current app version
const useVersion = () => `${appVersions[0].major}.${appVersions[0].minor}`;

// create general context provider
const GeneralContext = createContext<GeneralContextType>({
  railNavCollapsed: false,
  setRailNavCollapsed: () => {},
  impactPanelCollapsed: false,
  setImpactPanelCollapsed: () => {},
  fullWidthPage: false,
  setFullWidthPage: () => {},
  referralBanner: null,
  setReferralBanner: () => {},
  networkState: false,
  pointPrices: {
    pointPriceCategory1: 0,
    pointPriceCategory2: 0,
    pointPriceCategory3: 0,
    pointPriceCategory4: 0,
  },
  navigationCrumbs: { crumbs: [], additionalData: <></> },
  setNavigationCrumbs: () => {},
});

export const GeneralContextProvider = ({ children }: { children: ReactNode }) => {
  const { pointPrices } = usePointPrice();
  const signal = useCancelAsyncOnUnmount()();
  const networkState = useNetworkState();
  const version = useVersion();
  const appType = useAppType();
  const navigate = useNavigate();

  const { largeDesktop } = useAppType();
  const [navigationCrumbs, setNavigationCrumbs] = useState<NavigationCrumbs>({ crumbs: [], additionalData: <></> });

  const [railNavCollapsed, setRailNavCollapsed] = useState(!largeDesktop);
  const [impactPanelCollapsed, setImpactPanelCollapsed] = useState(() => getLocalStorageImpactPanelCollapsed());
  const [fullWidthPage, setFullWidthPage] = useState(false);
  const [referralBanner, setReferralBanner] = useState<{ text: string } | null>(null);

  // persist the impact panel collapse preference so it survives reload / navigation
  useEffect(() => {
    setLocalStorageImpactPanelCollapsed(impactPanelCollapsed);
  }, [impactPanelCollapsed]);

  // check if user has required version of mobile app
  const checkVersion = useCallback(() => {
    getMinVersion({
      signal,
    }).then((res) => {
      if (appType.app && parseInt(version.replaceAll(".", "")) < res.minAppVersion) {
        navigate("/update-app");
      }
    });
  }, [navigate, version, signal, appType]);

  useEffect(() => {
    checkVersion();
  }, []); // eslint-disable-line

  return (
    <GeneralContext.Provider
      value={{
        railNavCollapsed,
        setRailNavCollapsed,
        impactPanelCollapsed,
        setImpactPanelCollapsed,
        fullWidthPage,
        setFullWidthPage,
        referralBanner,
        setReferralBanner,
        networkState,
        pointPrices,
        navigationCrumbs,
        setNavigationCrumbs,
      }}
    >
      {children}
    </GeneralContext.Provider>
  );
};

export const GeneralContextConsumer = GeneralContext.Consumer;
export const useGeneralContext = () => useContext(GeneralContext);
